import type { ReactNode } from 'react';
import { unread, type Read } from './states/read.ts';
import { useStanding } from './states/stopped.ts';
import styles from './Empty.module.css';

/* ==========================================================================
   Empty / Error / Awaiting.

   One line of --text-mid text at the first row's position. An illustration, a
   headline and a centred call-to-action belong to a marketing page; here they
   push the data down and tell an operator something they already know.

   ---------------------------------------------------------------------------
   AN ABSENCE IS A CLAIM, AND A CLAIM NEEDS A READ BEHIND IT
   ---------------------------------------------------------------------------
   `No position in this window`, `No position fixes on record`, `No sessions
   for this robot` — every one of those says the platform was ASKED and
   answered with nothing. Printed over an endpoint that never answered they
   are the same lie as `0 faulted`, in prose, and they are worse than a blank
   because a blank is obviously missing and an absence reads as fine.

   So this primitive refuses to print the caller's sentence when the source
   behind it has not answered, exactly as `states/Blank` does — one line, one
   set of words, `Nothing has been read.` Two channels tell it:

     `of`        the read the sentence is a claim about. THE precise one: a
                 single endpoint dying under an otherwise healthy console is
                 the absence this console gets most wrong, and no console-wide
                 check can see it.
     the standing  the surface's own verdict, or the console-wide starvation
                 check, published by `states/stopped`. The safety net for the
                 call sites that do not hold a read.

   New code should reach for `states/Blank` instead: it owns the six house
   sentences, so a surface names a noun and a scope rather than writing its
   own phrasing. This one keeps working, and keeps quiet when it should.
   ========================================================================== */

export function Empty({
  children,
  detail,
  action,
  of,
  prompt = false,
}: {
  children: ReactNode;
  /** The mono half — a filter that is excluding everything, a time range. */
  detail?: ReactNode;
  /** At most one verb, right-aligned on the same line. */
  action?: ReactNode;
  /**
   * The read this absence is a claim about. Pass it and the sentence — and
   * the mono detail under it, which is a second claim — are withdrawn
   * whenever that source has not answered.
   */
  of?: Read | readonly Read[] | undefined;
  /**
   * THIS LINE IS NOT A CLAIM ABOUT DATA — it asks the operator to do
   * something. `Pick a row to read its transitions`, `Select a session.`, `No
   * robot selected.`
   *
   * Those are facts about the SELECTION, they are true whatever the platform
   * is doing, and they are the only useful thing the pane can say. Replacing
   * them with `Nothing has been read.` during an outage would delete the one
   * instruction on screen and answer a question nobody asked — the panel is
   * empty because nothing is selected, not because a read failed, and the
   * read's own failure is already stated by the panel it would be read from.
   *
   * Deliberately opt-IN, so the default stays honest: a sentence is treated as
   * a claim unless its author says otherwise.
   */
  prompt?: boolean | undefined;
}) {
  const standing = useStanding();
  const stopped =
    !prompt && ((of !== undefined && unread(of)) || standing.reason === 'stopped');
  return (
    <div className={styles.empty}>
      {stopped ? (
        <span>Nothing has been read.</span>
      ) : (
        <>
          <span>{children}</span>
          {detail !== undefined && <span className={styles.detail}>{detail}</span>}
          {action !== undefined && <span className={styles.action}>{action}</span>}
        </>
      )}
    </div>
  );
}

/** An inline failure row inside a panel, carrying the actual message. */
export function ErrorLine({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className={styles.empty} data-tone="error" role="alert">
      <span>{children}</span>
      {action !== undefined && <span className={styles.action}>{action}</span>}
    </div>
  );
}

/**
 * A capability the platform genuinely has no data for yet — live video before
 * the WebRTC path exists, a scan still being processed. Confident and honest:
 * it says what it is waiting for and what would make it appear. It is not a
 * mock of the eventual content.
 */
export function Awaiting({
  label,
  detail,
}: {
  label: string;
  detail?: ReactNode;
}) {
  return (
    <div className={styles.awaiting}>
      <span className={styles.awaitingLabel}>{label}</span>
      {detail !== undefined && <span className={styles.awaitingDetail}>{detail}</span>}
    </div>
  );
}
